import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Alert } from '@mui/material';
import { AlertTriangle } from 'react-feather';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button/Button';
import Header from '@/components/Header/Header';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useDebouncedUpdate } from '@/hooks/useDebouncedUpdate';
import { useNationalModifiersSaveState } from '@/hooks/useSaveButtonState';
import { useNationalModifiers } from '@/services/nationalModifiersService';
import { useUpdateNamedPriceModifierFlags } from '@/services/namedPrice/namedPrice.service';
import { NamedPriceModifierSelection } from '@/types/namedPrice.types';
import { NationalModifiersDetails } from '@/types/nationalModifiers.types';
import { SwitchCell } from './internal/SwitchCell';
import styles from './NamedPriceModifiers.module.css';

const toBoolean = (value: unknown) => (value != null ? Boolean(value) : false);

const NamedPriceModifiers: FC = () => {
  const { t } = useTranslation();
  const { data: namedPriceModifiers, isLoading, isError, refetch } = useNationalModifiers(true, 'namedPrice');
  const [data, setData] = useState<NationalModifiersDetails[]>([]);
  const [originalData, setOriginalData] = useState<NationalModifiersDetails[]>([]);
  const { mutate: updateNamedPriceModifierFlags } = useUpdateNamedPriceModifierFlags();

  const debouncedUpdate = useDebouncedUpdate(setData, 100);
  const isIdenticalData = useNationalModifiersSaveState(originalData, data);

  const columns = useMemo(
    () => [
      {
        id: 'isVisible',
        cell: SwitchCell,
        header: t('namedPriceModifiers.table.visibility'),
        accessorKey: 'isVisible',
        meta: {
          disableFilter: true,
          tooltipText: t('namedPriceModifiers.table.visibilityTooltip'),
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'isMandatory',
        cell: SwitchCell,
        header: t('namedPriceModifiers.table.mandatory'),
        accessorKey: 'isMandatory',
        meta: {
          disableFilter: true,
          tooltipText: t('namedPriceModifiers.table.mandatoryTooltip'),
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'isLock',
        cell: SwitchCell,
        header: t('namedPriceModifiers.table.lockPrice'),
        accessorKey: 'isLock',
        meta: {
          disableFilter: true,
          tooltipText: t('namedPriceModifiers.table.lockPriceTooltip'),
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'isZeroPriced',
        cell: SwitchCell,
        header: t('namedPriceModifiers.table.zeroPrice'),
        accessorKey: 'isZeroPriced',
        meta: {
          disableFilter: true,
          tooltipText: t('namedPriceModifiers.table.zeroPriceTooltip'),
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'slotName',
        accessorFn: (originalRow: NationalModifiersDetails) => originalRow.slotName || '-',
        header: t('common.slot'),
        meta: {
          tooltipText: t('namedPriceModifiers.table.slotNameTooltip'),
        },
        enableSorting: true,
        size: 180,
      },
      {
        id: 'namedPriceName',
        accessorFn: (originalRow: NationalModifiersDetails) => originalRow.namedPriceName || '-',
        header: t('namedPriceModifiers.table.namedPriceModifier'),
        meta: {
          tooltipText: t('namedPriceModifiers.table.namedPriceModifierTooltip'),
        },
        enableSorting: true,
        size: 400,
      },
    ],
    [t],
  );

  if (isError) {
    showToast({
      label: t('namedPriceModifiers.errorFetching'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingNamedPriceModifiers',
    });
  }

  useEffect(() => {
    if (namedPriceModifiers?.data) {
      setData(namedPriceModifiers.data);
      setOriginalData(namedPriceModifiers.data);
    }
  }, [namedPriceModifiers]);

  const onButtonClick = useCallback(() => {
    // Flush any pending debounced updates before saving
    const updateFn = debouncedUpdate as typeof debouncedUpdate & { flush?: () => void };

    if (updateFn.flush) {
      flushSync(() => {
        updateFn.flush!();
      });
    }

    const namedPriceModifiersToUpdate: NamedPriceModifierSelection[] = [];

    originalData.forEach((modifier) => {
      const resultObject = data.find((modifierInternal) => modifierInternal.id === modifier.id);

      if (!resultObject) return;

      const resultIsLock = toBoolean(resultObject.isLock);
      const resultIsVisible = toBoolean(resultObject.isVisible);
      const resultIsMandatory = toBoolean(resultObject.isMandatory);
      const resultIsZeroPriced = toBoolean(resultObject.isZeroPriced);

      if (
        resultIsLock !== toBoolean(modifier.isLock) ||
        resultIsVisible !== toBoolean(modifier.isVisible) ||
        resultIsMandatory !== toBoolean(modifier.isMandatory) ||
        resultIsZeroPriced !== toBoolean(modifier.isZeroPriced)
      ) {
        namedPriceModifiersToUpdate.push({
          namedPriceId: resultObject.namedPriceId ?? resultObject.id,
          isVisible: resultIsVisible,
          isMandatory: resultIsMandatory,
          isZeroPriced: resultIsZeroPriced,
          priceLock: resultIsLock,
        });
      }
    });

    if (namedPriceModifiersToUpdate.length > 0) {
      updateNamedPriceModifierFlags(namedPriceModifiersToUpdate, {
        onSuccess: () => {
          showToast({
            id: 'editNamedPriceModifiers',
            label: t('namedPriceModifiers.settingsUpdated'),
          });

          refetch();
        },
        onError: () => {
          showToast({
            id: 'errorUpdatingNamedPriceModifiers',
            label: t('namedPriceModifiers.errorUpdatingSettings'),
            toastType: ToastTypes.Error,
          });
        },
      });
    }
  }, [originalData, data, debouncedUpdate, updateNamedPriceModifierFlags, t, refetch]);

  return (
    <>
      <Header
        title={t('namedPriceModifiers.title')}
        breadcrumb={{ to: '/', label: t('namedPriceModifiers.breadcrumb') }}
      />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          {t('namedPriceModifiers.allModifiersHeader')}
          <Button disabled={isIdenticalData} onClick={onButtonClick}>
            {t('namedPriceModifiers.saveButton')}
          </Button>
        </div>
        <Alert
          data-testid="managed-categories-banner"
          icon={<AlertTriangle size={20} color="#E6740A" />}
          severity="warning"
          className={styles.banner}
          sx={{
            backgroundColor: '#FAE4D0',
            color: '#3D3D3D',
            border: '1px solid #E6740A',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 400,
            alignItems: 'center',
            padding: '11px 16px',
            minHeight: '42px',
            '& .MuiAlert-icon': {
              color: '#E6740A',
              padding: 0,
              marginRight: '12px',
              opacity: 1,
              alignItems: 'center',
            },
            '& .MuiAlert-message': {
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {t('namedPriceModifiers.managedCategoriesBanner')}
        </Alert>
        <TableCard
          height="calc(75vh - 100px)"
          isLoading={isLoading}
          data={data}
          initialSorting={[
            { id: 'slotName', desc: false },
            { id: 'namedPriceName', desc: false },
          ]}
          meta={{
            columns,
            data,
            updateData: debouncedUpdate,
          }}
          columns={columns}
          enableFilters
        />
      </div>
    </>
  );
};

export default NamedPriceModifiers;
